import { Component, OnInit, computed, effect, inject, signal } from '@angular/core';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { Observable, finalize, map } from 'rxjs';
import { reportInvalidFormFields } from '@app/utilities';
import { TranslateService } from '@ngx-translate/core';
import { BreadcrumbService } from '@card-software/library';
import { ApAdvancePayment, ApAdvancePaymentInsert, ApPaymentStatus, S3Object } from '../../../models/ap-advance/ap-advance';
import { ApAdvanceAllocationFormGroup, ApAdvanceFormGroup } from '../../../models/ap-advance/ap-advance-form';
import { Currency } from '../../../models/ap-document/ap-document';
import { ApAdvanceRequest, ApPaymentRequestStatus } from '../../../models/ap-advance-request/ap-advance-request';
import { ApAdvanceProxyService } from '../../../proxies/ap-advance.proxy.service';
import { PurchaseOrderProxyService, PURCHASE_ORDER_STATUS_CANCELLED } from '../../../proxies/purchase-order.proxy.service';
import { ApAdvanceRequestProxyService } from '../../../proxies/ap-advance-request.proxy.service';
import { BankAccountProxyService } from '../../../proxies/bank-account.proxy.service';
import { ExchangeRateProxyService } from '../../../proxies/exchange-rate.proxy.service';
import { BusinessProxyService } from '../../../proxies/business.proxy.service';
import { Business, BusinessEntityType } from '../../../models/business';
import { ProviderBankAccount, ProviderBankType } from '../../../models/provider/provider-details';
import { isConvertible } from '../../../models/currency-pairs';
import { BankAccount, BankAccountStatus } from '../../../models/bank-account/bank-account';
import { EnumService } from '../../../services/enum.service';
import { environment } from '../../../../environments/environment';

/** Which path started this advance. */
type AdvanceSource = 'direct' | 'request';

interface RequestOption {
  label: string;
  value: string;
}

interface SelectOption {
  label: string;
  value: string | number;
}

/**
 * One allocation row as the signal graph sees it.
 *
 * <p>Reactive forms are not signals: a `computed` that reads FormControl values has nothing to depend on,
 * so Angular evaluates it once â€” on the first render, before the record has loaded â€” and caches that
 * answer forever. That is what put a zero in the advance summary of every opened advance and left the
 * obligation panel hidden: the totals were computed against an empty FormArray and never recomputed.
 * `recomputeTotals` mirrors the rows into a signal instead, and everything downstream reads that.</p>
 */
interface AllocationSnapshot {
  purchaseOrderReference: string;
  purchaseOrderNumber: string;
  selected: boolean;
  allocatedAmount: number;
  /** The purchase order's currency; null until a PO fetch supplies it on an older saved allocation. */
  currency: Currency | null;
}

@Component({
  selector: 'app-ap-advance-details',
  templateUrl: './ap-advance-details.component.html',
  styleUrl: './ap-advance-details.component.scss',
})
export class ApAdvanceDetailsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly formBuilder = inject(FormBuilder);
  private readonly service = inject(ApAdvanceProxyService);
  private readonly purchaseOrderService = inject(PurchaseOrderProxyService);
  private readonly requestService = inject(ApAdvanceRequestProxyService);
  private readonly bankAccountService = inject(BankAccountProxyService);
  private readonly exchangeRateProxy = inject(ExchangeRateProxyService);
  private readonly businessService = inject(BusinessProxyService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly enumService = inject(EnumService);
  private readonly translate = inject(TranslateService);
  private readonly breadcrumbService = inject(BreadcrumbService);

  readonly ApPaymentStatus = ApPaymentStatus;

  isLoading = signal(0);
  isNew = signal(true);
  currentId = signal<string | null>(null);
  status = signal<number>(ApPaymentStatus.Draft);
  headerTitle = signal<string>(this.t('ApAdvance_New', 'New advance'));
  advanceNumber = signal<string>('');

  // ---- Accounting / GL impact (#5024 #5031) ----
  /** The journal entry this advance booked when it posted. Null on a draft. */
  readonly journalEntryReference = signal<string | null>(null);
  readonly journalEntryDocumentNumber = signal<string | null>(null);
  readonly showAccounting = computed(() => !!this.journalEntryReference());
  /** The component takes a list; an AP advance books exactly one entry. */
  readonly journalEntryReferences = computed(() => {
    const reference = this.journalEntryReference();
    return reference ? [reference] : [];
  });

  attachments = signal<S3Object[]>([]);
  autoCreated = signal(false);
  requestId = signal<string | null>(null);

  source = signal<AdvanceSource>('direct');
  currency = signal<Currency>(Currency.HNL);
  currencyOptions = signal<SelectOption[]>([]);
  private readonly allBankAccounts = signal<BankAccount[]>([]);

  // Request path: pick an Approved advance request, which opens (or spawns) its 1:1 Draft advance.
  approvedRequests = signal<ApAdvanceRequest[]>([]);
  readonly selectedRequestControl = new FormControl<string | null>(null);

  /**
   * What LEAVES the bank, in the source account's currency (#5373).
   *
   * <p>Not the same thing as what we owe, and it used to be: this held the allocation total, which is the
   * obligation. Fund a dollar order out of a Lempira account and the two are different numbers in
   * different currencies â€” so labelling the obligation with the account's currency made the requested
   * amount appear to change when the account changed. It never changes; only the cash does.</p>
   */
  readonly amount = computed<number>(() => {
    if (this.observedLeg() === 'departure') return this.observedAmountValue() ?? 0;
    // Nothing converts, or the obligation pins the departure: what leaves IS what we owe.
    return this.obligationTotal();
  });
  selectedCount = signal(0);
  totalPos = signal(0);
  // Bank charges (outgoing â†’ added on top of the advance amount).
  bankingFeeValue = signal(0);
  securityFeeValue = signal(0);
  readonly totalCashOut = computed(() => Math.round((this.amount() + this.bankingFeeValue() + this.securityFeeValue()) * 100) / 100);

  /** Last obligation currency the destination validator was synced against â€” see recomputeTotals. */
  private lastObligationCurrency: Currency | null = null;

  private readonly bankAccountMap = new Map<string, BankAccount>();
  private statusLabels: Record<number, string> = {};
  private currencyLabels: Record<number, string> = {};

  readonly isDraft = computed(() => this.status() === ApPaymentStatus.Draft);
  readonly isPersisted = computed(() => !this.isNew() && !!this.currentId());
  /** This advance was spawned from an approved request: its provider and PO allocation are fixed by the request. */
  readonly fromRequest = computed(() => !!this.requestId());
  readonly isRequestMode = computed(() => this.source() === 'request');
  readonly showRequestPicker = computed(() => this.isNew() && this.isRequestMode());
  readonly showAdvanceForm = computed(() => !this.showRequestPicker());
  readonly canEditCurrency = computed(() => this.isNew() && this.source() === 'direct');
  /** The provider's own accounts (#5143). */
  readonly providerBanks = signal<ProviderBankAccount[]>([]);

  /** The two conversion inputs, mirrored from the form so the computeds below react to typing (#5145). */
  private readonly selectedDestinationId = signal<string | null>(null);

  /** True when the advance was opened with a destination already on the record â€” decided upstream on
   *  the advance request, so this screen shows it rather than offering it. Deliberately not set from
   *  the control's value: the auto-select fallback fills that in too, and a bank this screen picked
   *  for you is exactly the one that should stay changeable. */
  private readonly destinationDecidedUpstream = signal(false);

  /**
   * Latched once the approved destination turns out to be one the post will refuse.
   *
   * <p>Latched rather than derived so the field does not lock itself again the moment a workable account
   * is chosen: having been sent here to fix the destination, the officer keeps the field until they leave
   * the screen.</p>
   */
  private readonly destinationUnlockedByConflict = signal(false);

  /**
   * Whether the destination is the request's to state and this screen's only to show.
   *
   * <p>It stops being so when the approved account cannot receive the advance: an Approved request can no
   * longer be edited (the API refuses), so keeping the field locked would strand the document with no
   * remedy in the app at all. The choice is still bounded to the same provider's own accounts, and the
   * conflict that opened the field stays on screen while it is open.</p>
   */
  readonly destinationLocked = computed(() => this.destinationDecidedUpstream() && !this.destinationUnlockedByConflict());
  private readonly observedAmountValue = signal<number | null>(null);

  /**
   * The currency the figure now in `observedAmount` was stated in (#5373).
   *
   * <p>The question moves with the currencies, and an answer to the old one is not an answer to the new
   * one. A request-spawned draft carries the obligation as its `amount`, so funding a dollar order out of a
   * Lempira account silently turned "USD 3,000 owed" into "HNL 3,000 sent" â€” a rate of 1.0 nobody typed,
   * on a screen whose whole job is to ask what left the bank. Cleared rather than converted: only the bank
   * statement knows what actually left.</p>
   *
   * <p>Null means not yet known â€” the arrival leg is stated in the destination's currency, which is not in
   * hand until the provider's accounts load. Nothing is cleared on the strength of not knowing.</p>
   */
  private readonly observedStatedIn = signal<Currency | null>(null);

  /** The purchase orders' currency, read off the saved allocations. Every allocation on one advance shares
   *  it â€” the API refuses a mixed-currency advance, because one stated figure cannot be apportioned across
   *  obligations in two currencies without a second rate nobody gave. */
  private readonly allocationCurrency = signal<Currency | null>(null);

  /**
   * The obligation's currency â€” the purchase orders', never the funding account's.
   *
   * <p>Read off the SELECTED rows first: the direct path lists every purchase order the provider has, and
   * those need not share a currency even though one advance's allocations must. Then the currency saved
   * with the allocations, then any row that knows its own, and only as a last resort ours.</p>
   */
  readonly obligationCurrency = computed<Currency>(() => {
    const rows = this.allocationSnapshot();
    return (
      rows.find((row) => row.selected && row.currency !== null)?.currency ??
      this.allocationCurrency() ??
      rows.find((row) => row.currency !== null)?.currency ??
      this.currency()
    );
  });

  /** For the template: the rate panel shows whenever the advance is not already in the books' currency. */
  protected readonly Currency = Currency;

  /**
   * Which of the provider's accounts this advance may reach (#5373).
   *
   * <p>Bounded by the same model as the source: at most two currencies across order, account and
   * destination, a pair the bank will convert, and never a foreign order paid into a dollar account â€” the
   * combination the business ruled out, so it is not offered rather than refused after the fact.</p>
   *
   * <p>Intermediaries are out for a different reason: a correspondent bank is a leg of the route, not where
   * the money comes to rest, and the API will not accept one as a destination (#5143).</p>
   */
  readonly providerBankOptions = computed<SelectOption[]>(() => {
    const source = this.currency();
    // The account the request approved. It is on the document already, so it is not the source account's
    // to veto: filtered out, the select had no option matching the id it was given, rendered as though
    // nothing was chosen, and the screen told the officer this provider had no account to receive the
    // money â€” about an advance whose destination was decided upstream precisely so it could not be
    // re-decided here.
    const decided = this.destinationDecidedUpstream() ? this.selectedDestinationId() : null;

    return this.providerBanks()
      .filter((bank) => {
        if (!bank.id || bank.bankType === ProviderBankType.Intermediary) return false;
        if (bank.id === decided) return true;
        const destination = bank.currency as Currency;
        if (!isConvertible(source, destination)) return false;
        // The dollar account of a provider with a Euro order stays on the list (Â§8) â€” it is allowed, and
        // where they also hold a Euro one the conflict below says which to use rather than hiding either.
        return this.canReceive(destination);
      })
      .map((bank) => ({ label: this.providerBankLabel(bank), value: bank.id! }));
  });

  /**
   * Whether an account in this currency can receive this advance â€” the rule `AdvanceLeg` applies at post.
   *
   * <p>Mirrored here so the refusal is visible on the screen that caused it, rather than arriving as a
   * toast after a round-trip. One rule now: three currencies pin neither leg. The foreign-order-into-a-
   * dollar-account refusal was narrowed to "a better account exists" and depends on the provider's OTHER
   * accounts, so it lives in {@link betterAccount} instead.</p>
   */
  private canReceive(destination: Currency): boolean {
    // Three currencies pin neither leg, and the bank only quotes between its own two. A foreign order paid
    // into a dollar account is NOT refused here (Â§8): the leg is an ordinary departure and the rate falls
    // out of what left â€” see betterAccount for the one case that is still wrong.
    return new Set([this.obligationCurrency(), this.currency(), destination]).size <= 2;
  }

  /**
   * The provider's own account in the obligation's currency, when one exists and is not the one aimed at.
   *
   * <p>Mirrors the API's narrowed rule (Â§8): paying a Euro order into the provider's dollar account is
   * ordinary trade practice and is allowed, but if they also hold a Euro account then the dollar one was
   * almost certainly picked by accident â€” that is how it got picked, being flagged primary.</p>
   */
  private readonly betterAccount = computed(() => {
    const destination = this.destinationAccount();
    const obligation = this.obligationCurrency();
    if (!destination || (destination.currency as Currency) === obligation) return undefined;
    if ((destination.currency as Currency) !== this.functionalCurrency()) return undefined;
    if (obligation === this.functionalCurrency()) return undefined;

    return this.providerBanks().find(
      (bank) =>
        bank.id &&
        bank.bankType !== ProviderBankType.Intermediary &&
        (bank.currency as Currency) === obligation &&
        isConvertible(this.currency(), bank.currency as Currency),
    );
  });

  /**
   * Why this advance cannot go out to the account it is aimed at, when that is so (#5373).
   *
   * <p>The destination is decided when the request is approved, and that screen offers every account the
   * provider has â€” so a EUR order can be approved against a USD account and nothing says otherwise until
   * the post is refused. This says it on arrival, and, where the provider has an account that WOULD work,
   * names it: the refusal is nearly always the wrong account rather than a missing one.</p>
   */
  readonly destinationConflict = computed<string | null>(() => {
    const destination = this.destinationAccount();
    if (!destination) return null;
    const arrival = destination.currency as Currency;
    const obligation = this.currencyLabel(this.obligationCurrency());

    if (!this.canReceive(arrival)) {
      return this.t(
        'ApAdvance_Conflict_ThreeCurrencies',
        'This advance would involve three currencies: a {obligation} purchase order, paid from a {source} account, into a {arrival} account. Pay it from an account in {obligation} or {arrival}, so there is one conversion rather than two.',
      )
        .replace(/{obligation}/g, obligation)
        .replace(/{source}/g, this.currencyLabel(this.currency()))
        .replace(/{arrival}/g, this.currencyLabel(arrival));
    }

    const better = this.betterAccount();
    if (!better) return null;

    return this.t(
      'ApAdvance_Conflict_BetterAccount',
      'This purchase order is in {obligation} and this is the provider\u2019s {arrival} account â€” but they also hold one in {obligation}. Pay it into that one: {account}.',
    )
      .replace(/{obligation}/g, obligation)
      .replace(/{arrival}/g, this.currencyLabel(arrival))
      .replace('{account}', this.providerBankLabel(better));
  });

  /** The chosen destination account, when one is chosen â€” the other end of the transfer. */
  readonly destinationAccount = computed<ProviderBankAccount | undefined>(() =>
    this.providerBanks().find((bank) => bank.id === this.selectedDestinationId()),
  );

  /**
   * True when the money changes currency on the way (#5145): we send one currency and the provider's
   * account holds another. That is a real bank conversion â€” unlike settling an invoice in another currency,
   * where nothing converts at all.
   */
  readonly destinationConverts = computed<boolean>(() => {
    const destination = this.destinationAccount();
    return !!destination && destination.currency !== this.currency();
  });

  /** The currency the arrival figure is entered in â€” the provider's, not ours. */
  readonly destinationCurrencyLabel = computed<string>(() => {
    const destination = this.destinationAccount();
    return destination ? (this.currencyLabels[destination.currency as Currency] ?? '') : '';
  });

  /**
   * Which leg the officer has to state (#5373) â€” mirrors `AdvanceLeg` on the API.
   *
   * <p>The obligation pins one end of the transfer; the other is the question. Owing Lempiras from a dollar
   * account, the provider must receive every Lempira owed, so what LEFT is asked. Owing dollars from a
   * dollar account into a provider banking in Lempiras, USD 500 leaves and what ARRIVED is asked.</p>
   */
  readonly observedLeg = computed<'none' | 'departure' | 'arrival'>(() => {
    const obligation = this.obligationCurrency();
    const source = this.currency();
    const arrival = this.destinationAccount()?.currency ?? source;

    // Nothing converts: one leg, not two. Still a question when the obligation is in neither â€” a dollar
    // order settled Lempiras-to-Lempiras, where nobody observed a rate and the order cannot supply one.
    if (source === arrival) return obligation === source ? 'none' : 'departure';
    return obligation === arrival ? 'departure' : 'arrival';
  });

  /**
   * The allocation rows, mirrored out of the FormArray so the totals below can react to them.
   *
   * <p>See {@link AllocationSnapshot}: a computed over FormControl values is evaluated once and cached,
   * which is why every one of these figures used to read 0 on a loaded advance. `recomputeTotals` is
   * already wired to the array's `valueChanges`, so this refreshes on load, on a checkbox and on typing.</p>
   */
  private readonly allocationSnapshot = signal<AllocationSnapshot[]>([]);

  /** What we owe, summed across the allocations â€” in the obligation's currency, never restated. */
  readonly obligationTotal = computed<number>(
    () => Math.round(this.allocationSnapshot().reduce((total, row) => total + (row.selected ? row.allocatedAmount : 0), 0) * 100) / 100,
  );

  /** The currency the officer types the observed figure in. */
  readonly observedCurrency = computed<Currency>(() =>
    this.observedLeg() === 'arrival' ? ((this.destinationAccount()?.currency as Currency) ?? this.currency()) : this.currency(),
  );

  /**
   * The rate that falls out of what was stated, against what we owe.
   *
   * <p>Not decoration: this <b>is</b> the rate the books will use. Nothing else supplies it, so a
   * mistyped amount is a silently wrong rate on the Anticipo.</p>
   */
  readonly derivedRate = computed<number | null>(() => {
    if (this.observedLeg() === 'none') return null;
    const observed = this.observedAmountValue();
    const owed = this.obligationTotal();
    if (!observed || observed <= 0 || owed <= 0) return null;
    const perObligation = observed / owed;
    return Math.round((perObligation < 1 ? 1 / perObligation : perObligation) * 1e6) / 1e6;
  });

  /** How the derived rate reads: obligation â†’ observed, in the officer's own terms. */
  readonly derivedRateLabel = computed<string>(() => {
    const rate = this.derivedRate();
    if (rate === null) return '';
    const owed = this.obligationTotal();
    const observed = this.observedAmountValue() ?? 0;
    const [strong, weak] =
      observed / owed < 1
        ? [this.currencyLabel(this.observedCurrency()), this.currencyLabel(this.obligationCurrency())]
        : [this.currencyLabel(this.obligationCurrency()), this.currencyLabel(this.observedCurrency())];
    return `1 ${strong} = ${rate} ${weak}`;
  });

  /** The books' currency, for the approved-value column. USD everywhere today. */
  readonly functionalCurrency = signal<Currency>(Currency.USD);

  /**
   * What the summary is waiting for, when it is waiting for something.
   *
   * <p>Once a leg has to be stated the summary cannot fill itself in â€” the amount leaving the bank is not
   * derivable from the obligation without the rate, and the rate is derived from that very amount. Saying
   * so beats a bare 0.00 that reads as "this advance is worth nothing".</p>
   */
  readonly summaryHint = computed<string>(() => {
    // Only the departure leg: on the arrival leg what leaves IS the obligation, so the figures below are
    // already right and it is the rate, not the summary, that is still waiting on the officer.
    if (this.observedLeg() === 'departure' && (this.observedAmountValue() ?? 0) <= 0) {
      return this.t(
        'ApAdvance_Summary_AwaitingSent',
        'Enter the amount sent from the bank above â€” the exchange rate and this summary are calculated from it.',
      );
    }
    return this.t(
      'ApAdvance_Summary_Hint',
      'Total cash out is the amount sent plus banking and security fees, in the bank account currency.',
    );
  });

  /** Per-order rates, as saved on the allocations. */
  private readonly allocationRates = signal<Map<string, { rate: number; functionalAmount: number }>>(new Map());

  /** What we owe, order by order, with the rate each was struck at. */
  readonly obligationBreakdown = computed(() =>
    this.allocationSnapshot()
      .filter((row) => row.selected && row.allocatedAmount > 0)
      .map((row) => {
        const saved = this.allocationRates().get(row.purchaseOrderReference);
        return {
          purchaseOrderNumber: row.purchaseOrderNumber || row.purchaseOrderReference,
          amount: row.allocatedAmount,
          rate: saved?.rate ? Math.round((1 / saved.rate) * 1e4) / 1e4 : 0,
          functionalAmount: saved?.functionalAmount ?? 0,
        };
      })
      .filter((row) => row.rate > 0),
  );

  /** What the advance was approved to be worth, summed across the orders. */
  readonly approvedFunctionalTotal = computed(
    () => Math.round(this.obligationBreakdown().reduce((t, r) => t + r.functionalAmount, 0) * 100) / 100,
  );

  /** The published rate for the same pair, so the derived one can be read against something. */
  readonly dayRate = signal<number | null>(null);

  /** The non-dollar side of the pair â€” the only one the rate table has a quote for. */
  private readonly quotedCurrency = computed<Currency | null>(() => {
    const pair = [this.obligationCurrency(), this.observedCurrency()].filter((c) => c !== Currency.USD);
    return pair.length === 1 ? pair[0] : null;
  });

  /** How far the derived rate sits from the day's, as a fraction. */
  readonly rateDeviation = computed<number | null>(() => {
    const derived = this.derivedRate();
    const day = this.dayRate();
    if (derived === null || !day || day <= 0) return null;
    return (derived - day) / day;
  });

  /**
   * Whether the gap is big enough to be a mistake rather than the market.
   *
   * <p>A rate drifting a fraction of a percent from the table is normal and gets the ordinary treatment.
   * A slipped decimal is off by an order of magnitude, balances perfectly, and is invisible afterwards â€”
   * so it gets a warning of its own rather than sharing the informational one.</p>
   */
  readonly rateLooksWrong = computed<boolean>(() => {
    const deviation = this.rateDeviation();
    return deviation !== null && Math.abs(deviation) > 0.1;
  });

  /**
   * Which of our accounts may fund this advance (#5373).
   *
   * <p>This used to offer only accounts already in the advance's own currency, which on a request-spawned
   * draft is the purchase order's. That made the two most ordinary local combinations unreachable â€” a
   * dollar order settled out of a Lempira account, and a dollar order paid to a provider banking in
   * Lempiras â€” because no Lempira account was ever in the list to pick.</p>
   *
   * <p><b>The source drives the advance's currency, not the other way round.</b> What may fund it is
   * bounded by the model instead: at most two currencies across order, account and destination, and a pair
   * the bank will actually convert.</p>
   */
  readonly bankAccountOptions = computed<SelectOption[]>(() => {
    const obligation = this.obligationCurrency();
    const destination = this.destinationAccount()?.currency as Currency | undefined;

    return this.allBankAccounts()
      .filter((acc) => {
        if (!acc.id) return false;
        const source = acc.currency as Currency;
        // Three currencies cannot be measured: the obligation would pin neither leg (Â§3).
        if (new Set([obligation, source, destination ?? source]).size > 2) return false;
        // And the bank has to be willing to make that conversion.
        return destination === undefined || isConvertible(source, destination);
      })
      .map((acc) => ({ label: this.bankLabel(acc), value: acc.id! }));
  });

  readonly form: FormGroup<ApAdvanceFormGroup> = this.formBuilder.group<ApAdvanceFormGroup>({
    provider: new FormControl<Business | null>(null, [Validators.required]),
    paymentDate: new FormControl<Date | null>(new Date(), [Validators.required]),
    bankAccount: new FormControl<string | null>(null, [Validators.required]),
    // Not required: most providers have no bank accounts on file (#5143).
    selectedProviderBankId: new FormControl<string | null>(null),
    // Optional always (#5373): left empty the API takes the rate in force on the payment date, or the
    // purchase order's own where nothing converts. Filled in, it is the rate the bank actually gave, and
    // it wins â€” the books have to agree with the statement.
    // Whichever leg the currencies make the question (#5373). Required once there is one to answer;
    // syncDestinationValidator applies that.
    observedAmount: new FormControl<number | null>(null),
    bankTransactionReference: new FormControl<string | null>(null),
    currency: new FormControl<Currency>(Currency.HNL, { nonNullable: true }),
    bankingFee: new FormControl<number | null>(0),
    securityFee: new FormControl<number | null>(0),
    notes: new FormControl<string | null>(null),
    allocations: this.formBuilder.array<FormGroup<ApAdvanceAllocationFormGroup>>([]),
  });

  get allocations(): FormArray<FormGroup<ApAdvanceAllocationFormGroup>> {
    return this.form.controls.allocations;
  }

  /** Read per call, not fixed: which figure `observedAmount` holds â€” and so what a toast should call it
   *  when it is missing â€” follows from the currencies (#5373). */
  private get fieldLabels(): Record<string, string> {
    return {
      provider: 'ApPayment_Provider',
      paymentDate: 'ApPayment_Date',
      bankAccount: 'ApPayment_BankAccount',
      allocations: 'ApAdvance_Allocations',
      observedAmount: this.observedLeg() === 'arrival' ? 'ApAdvance_AmountReceived' : 'ApAdvance_AmountSent',
    };
  }

  constructor() {
    this.statusLabels = this.enumService.getEnum('ApPaymentStatus').values as Record<number, string>;
    this.currencyLabels = this.enumService.getEnum('Currency').values as Record<number, string>;
    this.currencyOptions.set(Object.entries(this.currencyLabels).map(([k, v]) => ({ label: v, value: Number(k) })));

    this.allocations.valueChanges.subscribe(() => this.recomputeTotals());
    this.form.controls.bankingFee.valueChanges.subscribe((v) => this.bankingFeeValue.set(v ?? 0));
    this.form.controls.securityFee.valueChanges.subscribe((v) => this.securityFeeValue.set(v ?? 0));
    this.form.controls.currency.valueChanges.subscribe((c) => this.onCurrencyChange(c));
    // The two halves of a conversion (#5145). Mirrored into signals so destinationConverts / derivedRate
    // recompute as the officer picks an account and types what arrived.
    this.form.controls.selectedProviderBankId.valueChanges.subscribe((id) => {
      this.selectedDestinationId.set(id);
      this.syncDestinationValidator();
    });
    this.form.controls.observedAmount.valueChanges.subscribe((v) => this.observedAmountValue.set(v));
    this.form.controls.bankAccount.valueChanges.subscribe((id) => this.onBankAccountChange(id));

    effect(() => this.breadcrumbService.setCurrent(this.isNew() ? 'Common_New' : this.advanceNumber()), { allowSignalWrites: true });
  }

  ngOnInit(): void {
    this.loadBankAccounts();
    const id = this.route.snapshot.queryParamMap.get('id');
    const source = this.route.snapshot.queryParamMap.get('source');
    if (source === 'request') this.source.set('request');

    if (id && id !== 'new') {
      this.isNew.set(false);
      this.currentId.set(id);
      this.loadAdvance(id);
      return;
    }

    if (this.isRequestMode()) this.loadApprovedRequests();
  }

  // ---- helpers ----
  private t(key: string, fallback: string): string {
    const v = this.translate.instant(key);
    return v === key ? fallback : v;
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  currencyLabel(currency: number | null | undefined): string {
    if (currency === null || currency === undefined) return '';
    return this.currencyLabels[currency] ?? '';
  }

  statusLabelText(): string {
    return this.statusLabels[this.status()] ?? '';
  }

  statusSeverity(): 'success' | 'info' | 'secondary' {
    return this.status() === ApPaymentStatus.Posted ? 'info' : 'secondary';
  }

  // ---- bank accounts ----
  private loadBankAccounts(): void {
    this.bankAccountService.getAll().subscribe({
      next: (response) => {
        const accounts = (response.data ?? []).filter((a) => a.status === BankAccountStatus.ACTIVE || !this.isNew());
        for (const acc of accounts) {
          if (acc.id) this.bankAccountMap.set(acc.id, acc);
        }
        this.allBankAccounts.set(accounts);
      },
      error: () => this.allBankAccounts.set([]),
    });
  }

  /**
   * Nothing to require any more (#5373): the rate is optional everywhere, because the API has a rate for
   * every case â€” the bank's when it is given, the purchase order's where nothing converted, the day's
   * otherwise. Kept as a hook so the destination change still has somewhere to settle.
   */
  /**
   * Fetches the published rate for the pair, purely so the derived one can be read against something.
   *
   * <p>It never becomes the rate used â€” that is whatever the stated amount implies. It is here because a
   * slipped decimal produces a perfectly balanced entry at a tenth of the right value, and the only cheap
   * way to notice is to have the ordinary figure sitting next to it.</p>
   */
  private loadDayRate(): void {
    const quoted = this.quotedCurrency();
    const on = this.form.controls.paymentDate.value;
    if (quoted === null || this.observedLeg() === 'none' || !on) {
      this.dayRate.set(null);
      return;
    }
    this.exchangeRateProxy.getRateInForce(quoted, on).subscribe({
      next: (response) => {
        const perUsd = response?.data?.averageRateToUSD ?? response?.data?.rateToUSD ?? null;
        // Oriented the way derivedRate reads it: the larger figure is the multiplier either way.
        this.dayRate.set(perUsd && perUsd > 0 ? (perUsd >= 1 ? perUsd : 1 / perUsd) : null);
      },
      error: () => this.dayRate.set(null),
    });
  }

  private syncDestinationValidator(): void {
    const control = this.form.controls.observedAmount;
    if (this.observedLeg() === 'none') {
      // Nothing to state, so nothing to require â€” but the figure is left where it is. This runs once with
      // the provider's accounts still in flight, when every advance briefly looks like a same-currency
      // transfer; clearing here threw away the arrival amount of every saved draft as it loaded. What is
      // unused cannot leak either: buildInsert reads the leg, not the control.
      control.removeValidators(Validators.required);
    } else {
      const statedIn = this.observedCurrency();
      // A figure stated in another currency answers a question this screen is no longer asking.
      if (control.value !== null && this.observedStatedIn() !== null && this.observedStatedIn() !== statedIn) {
        this.setObservedAmount(null);
      }
      this.observedStatedIn.set(statedIn);
      control.addValidators(Validators.required);
    }
    control.updateValueAndValidity({ emitEvent: false });
    this.syncDestinationLock();
    this.loadDayRate();
  }

  /**
   * Opens the destination field when the approved account cannot be used, and keeps it shut otherwise.
   *
   * <p>Runs wherever the currencies can move â€” the accounts loading, a source account being picked, the
   * allocation changing â€” because the conflict is a consequence of all three.</p>
   */
  private syncDestinationLock(): void {
    // A posted advance is disabled whole; nothing here re-opens one field of it.
    if (!this.isDraft()) return;
    if (this.destinationConflict()) this.destinationUnlockedByConflict.set(true);

    const control = this.form.controls.selectedProviderBankId;
    if (this.destinationLocked()) {
      if (control.enabled) control.disable({ emitEvent: false });
    } else if (control.disabled) {
      control.enable({ emitEvent: false });
    }
  }

  /** Writes both halves of the observed amount: the control the officer sees and the signal the summary
   *  and the derived rate are computed from. The patch is silent, so the `valueChanges` mirror would not
   *  fire and the old figure would keep feeding the summary. */
  private setObservedAmount(value: number | null): void {
    this.form.controls.observedAmount.setValue(value, { emitEvent: false });
    this.observedAmountValue.set(value);
  }

  private onCurrencyChange(currency: Currency): void {
    this.currency.set(currency);
    // Changing OUR currency can turn a conversion into a same-currency transfer or the other way round.
    this.syncDestinationValidator();
  }

  /**
   * The account funding the advance sets the advance's currency (#5373).
   *
   * <p>Money leaves in whatever the account holds; the advance is denominated in that, and what it settles
   * stays denominated in the purchase order. Clearing the destination when the pair stops being convertible
   * is left to the picker, which simply stops offering it.</p>
   */
  private onBankAccountChange(bankAccountId: string | null): void {
    const account = bankAccountId ? this.bankAccountMap.get(bankAccountId) : undefined;
    if (!account) return;
    const currency = account.currency as Currency;
    if (currency === this.currency()) return;

    this.currency.set(currency);
    this.form.controls.currency.setValue(currency, { emitEvent: false });

    // A destination the new source can no longer reach stops being offered, so it cannot stay selected â€”
    // unless the request decided it, in which case it outranks the account we happen to be paying from.
    const chosen = this.form.controls.selectedProviderBankId.value;
    if (!this.destinationLocked() && chosen && !this.providerBankOptions().some((option) => option.value === chosen)) {
      this.form.controls.selectedProviderBankId.setValue(null);
    }
    this.syncDestinationValidator();
  }

  private bankLabel(acc: BankAccount): string {
    const cur = this.currencyLabels[acc.currency] ?? '';
    const last4 = (acc.bankAccountNumber ?? '').slice(-4);
    return [acc.bankName, cur, last4 ? `****${last4}` : ''].filter((s) => s).join(' Â· ');
  }

  // ---- provider ----
  public fetchProviders = (query: string): Observable<Business[]> =>
    this.businessService
      .search({ name: query, code: query, businessTypes: [BusinessEntityType.Provider] }, 1, 8, undefined, undefined, true)
      .pipe(map((response) => response.data?.items ?? []));

  onProviderSelected(provider: Business): void {
    this.applyProviderBanks(provider);
    if (provider?.id) this.loadProviderPurchaseOrders(provider.id);
  }

  onProviderCleared(): void {
    this.providerBanks.set([]);
    this.form.controls.selectedProviderBankId.setValue(null);
    this.allocations.clear();
    this.recomputeTotals();
  }

  /** Loads the provider's accounts, fetching by id when the search result did not carry them. */
  private applyProviderBanks(provider: Business | null, autoSelect = true): void {
    const banks = provider?.providerDetails?.banks ?? [];
    if (banks.length > 0 || !provider?.id) {
      this.setProviderBanks(banks, autoSelect);
      return;
    }
    this.businessService.getBusinessById(provider.id).subscribe({
      next: (response) => this.setProviderBanks(response.data?.providerDetails?.banks ?? [], autoSelect),
      error: () => this.setProviderBanks([], autoSelect),
    });
  }

  private setProviderBanks(banks: ProviderBankAccount[], autoSelect = true): void {
    this.providerBanks.set(banks);
    // Whether the destination converts is only answerable once the accounts are in hand â€” on a loaded
    // advance the id is already set, so nothing else would fire the check and the required amount would go
    // unvalidated (#5145).
    this.selectedDestinationId.set(this.form.controls.selectedProviderBankId.value);
    this.syncDestinationValidator();
    // An OPENED advance has already had its destination decided, and the id is patched in from the record
    // before the accounts arrive. Auto-selecting here would overwrite it with the provider's primary
    // account â€” the reason a saved advance could display a bank nobody chose. Mirrors ap-payment-details.
    if (!autoSelect) return;
    if (this.form.controls.selectedProviderBankId.value) return;
    // Same currency first: it needs no conversion, so it is the choice that asks the officer for nothing.
    // A convertible destination is offered in the picker but never auto-selected (#5145) â€” selecting one
    // silently would commit someone to recording what arrived without their having decided to convert.
    const usable = banks.filter((b) => b.id && b.currency === this.currency() && b.bankType !== ProviderBankType.Intermediary);
    const primary = usable.find((b) => b.isPrimary) ?? usable[0];
    if (primary?.id) this.form.controls.selectedProviderBankId.setValue(primary.id);
  }

  /**
   * The destination account, with its currency (#5373).
   *
   * <p>Not cosmetic: the destination's currency decides which leg of the transfer the officer is asked to
   * state, and whether the combination is supported at all. Without it on the label a reader cannot tell a
   * Euro account from a dollar one, and both are plausible for the same IBAN.</p>
   */
  private providerBankLabel(bank: ProviderBankAccount): string {
    return [bank.alias || bank.bankName, bank.accountNumber, this.currencyLabel(bank.currency as Currency)]
      .filter((part) => !!part)
      .join(' Â· ');
  }

  // ---- request path ----
  readonly requestOptions = computed<RequestOption[]>(() =>
    this.approvedRequests().map((r) => ({
      label: `${r.number} Â· ${r.providerLegalName} Â· ${r.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${this.currencyLabel(r.currency)}`,
      value: r.id ?? '',
    })),
  );

  private loadApprovedRequests(): void {
    this.startLoading();
    this.requestService
      .search({ status: ApPaymentRequestStatus.Approved }, 1, 100)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => this.approvedRequests.set(response.data?.items ?? []),
        error: () => this.approvedRequests.set([]),
      });
  }

  onRequestSelected(requestId: string | null): void {
    if (!requestId) return;
    this.startLoading();
    this.requestService
      .createOrGetAdvance(requestId)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          const advance = response.data;
          if (!advance?.id) return;
          this.isNew.set(false);
          this.currentId.set(advance.id);
          this.router.navigate([], { queryParams: { id: advance.id }, replaceUrl: true });
          this.patchFromAdvance(advance);
        },
        error: (error) => console.error('Error opening advance for request:', error),
      });
  }

  // ---- purchase orders / allocations ----
  private loadProviderPurchaseOrders(providerId: string): void {
    this.startLoading();
    this.purchaseOrderService
      .search({ providerReference: providerId }, 1, 200)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          this.allocations.clear();
          // Never offer a cancelled PO for an advance (#4516).
          const pos = (response.data?.items ?? []).filter((po) => po.status !== PURCHASE_ORDER_STATUS_CANCELLED);
          if (pos.length && pos[0].currency != null) {
            // The orders fix what we OWE. They also seed the advance's own currency, but only as a
            // starting point â€” choosing a funding account overrides that, and the obligation stays put.
            this.allocationCurrency.set(pos[0].currency);
            this.currency.set(pos[0].currency);
            this.form.controls.currency.setValue(pos[0].currency, { emitEvent: false });
          }
          for (const po of pos) {
            const ordered = (po.items ?? []).reduce((sum, i) => sum + (i.totalPrice ?? 0), 0);
            this.allocations.push(
              this.formBuilder.group<ApAdvanceAllocationFormGroup>({
                purchaseOrderReference: new FormControl<string>(po.id ?? '', { nonNullable: true }),
                purchaseOrderNumber: new FormControl<string>(po.purchaseOrderNumber ?? '', { nonNullable: true }),
                selected: new FormControl<boolean>(false, { nonNullable: true }),
                allocatedAmount: new FormControl<number>(0, { nonNullable: true }),
                ordered: new FormControl<number>(Math.round(ordered * 100) / 100, { nonNullable: true }),
                // The order's own, not the advance's: this list is every PO the provider has, and they
                // need not share a currency even though one advance's allocations must.
                currency: new FormControl<Currency | null>((po.currency as Currency) ?? null),
              }),
            );
          }
          this.recomputeTotals();
        },
        error: () => {
          this.allocations.clear();
          this.recomputeTotals();
        },
      });
  }

  /**
   * Mirrors the allocation rows into the signal graph.
   *
   * <p>The one place the FormArray is read for totals. Called on every `valueChanges` of the array and
   * wherever rows are built or hydrated, because nothing else would tell a `computed` that a form it
   * cannot observe has changed.</p>
   */
  private recomputeTotals(): void {
    const rows: AllocationSnapshot[] = this.allocations.controls.map((row) => ({
      purchaseOrderReference: row.controls.purchaseOrderReference.value,
      purchaseOrderNumber: row.controls.purchaseOrderNumber.value,
      selected: row.controls.selected.value,
      allocatedAmount: row.controls.allocatedAmount.value ?? 0,
      currency: row.controls.currency.value,
    }));
    this.allocationSnapshot.set(rows);
    this.selectedCount.set(rows.filter((row) => row.selected).length);
    this.totalPos.set(rows.length);

    // Which leg the officer is asked for follows from the obligation's currency, so selecting a different
    // order can change the question. Only on an actual change: this runs on every keystroke in an amount,
    // and syncing fetches the day's rate.
    const obligation = this.obligationCurrency();
    if (obligation !== this.lastObligationCurrency) {
      this.lastObligationCurrency = obligation;
      this.syncDestinationValidator();
    }
  }

  // ---- load existing ----
  private loadAdvance(id: string): void {
    this.startLoading();
    this.service
      .getById(id)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          if (response.data) this.patchFromAdvance(response.data);
        },
        error: (error) => console.error('Error loading advance:', error),
      });
  }

  private patchFromAdvance(data: ApAdvancePayment): void {
    this.status.set(data.status);
    this.currency.set(data.currency);
    this.advanceNumber.set(data.number ?? '');
    this.headerTitle.set(`${this.t('ApAdvance', 'Advance')} ${data.number ?? ''}`.trim());
    this.attachments.set(data.attachments ?? []);
    this.autoCreated.set(data.autoCreated ?? false);
    this.requestId.set(data.apAdvanceRequestReference ?? null);
    this.journalEntryReference.set(data.journalEntryReference ?? null);
    this.journalEntryDocumentNumber.set(data.journalEntryDocumentNumber ?? null);

    this.form.patchValue(
      {
        provider: {
          id: data.providerReference,
          name: data.providerName ?? '',
          code: data.providerCode,
          businessTypes: [BusinessEntityType.Provider],
        } as Business,
        paymentDate: data.paymentDate ? new Date(data.paymentDate) : null,
        bankAccount: data.bankAccountReference ?? null,
        selectedProviderBankId: data.selectedProviderBankId ?? null,
        observedAmount: data.destinationAmount ?? data.amount ?? null,
        bankTransactionReference: data.bankTransactionNumber ?? null,
        currency: data.currency,
        bankingFee: data.transferFee ?? 0,
        securityFee: data.securityTax ?? 0,
        notes: data.notes ?? null,
      },
      { emitEvent: false },
    );
    this.bankingFeeValue.set(data.transferFee ?? 0);
    this.securityFeeValue.set(data.securityTax ?? 0);
    // patchValue above is emitEvent:false, so the mirrored signals do not follow it (#5145).
    this.observedAmountValue.set(data.destinationAmount ?? data.amount ?? null);
    // Which currency that figure is in, so syncDestinationValidator can tell an answer from a leftover: a
    // destinationAmount was stated in the destination's currency (unknown until the accounts load, hence
    // null), and `amount` in the advance's own. On a request-spawned draft `amount` is the obligation,
    // which is nobody's answer to what left the bank â€” and the sync below drops it once the leg says so.
    this.observedStatedIn.set(data.destinationAmount != null ? null : data.currency);

    // The provider above is a synthetic Business carrying only id/name/code, and the patch is silent, so
    // nothing on the load path would fetch the provider's accounts: every OPENED advance came up with an
    // empty destination picker, the patched id matching no option, and rendered as though the provider had
    // no account in the currency. Never auto-selects â€” see setProviderBanks.
    this.destinationDecidedUpstream.set(!!data.selectedProviderBankId);
    this.destinationUnlockedByConflict.set(false);
    // Decided when the request was approved â€” this screen shows it rather than offering it, so the
    // control is disabled and reads like the other fields it is not the officer's to change.
    if (data.selectedProviderBankId) this.form.controls.selectedProviderBankId.disable({ emitEvent: false });
    if (data.providerReference) {
      this.applyProviderBanks({ id: data.providerReference } as Business, false);
    }

    this.allocations.clear();
    // The obligation's currency travels with the allocations (#5373); it is not the advance's own.
    this.allocationCurrency.set((data.allocations ?? [])[0]?.currency ?? null);
    this.functionalCurrency.set(data.functionalCurrency ?? Currency.USD);
    this.allocationRates.set(
      new Map(
        (data.allocations ?? [])
          .filter((a) => (a.exchangeRate ?? 0) > 0)
          .map((a) => [a.purchaseOrderReference, { rate: a.exchangeRate!, functionalAmount: a.functionalAmount ?? 0 }]),
      ),
    );
    for (const alloc of data.allocations ?? []) {
      this.allocations.push(
        this.formBuilder.group<ApAdvanceAllocationFormGroup>({
          purchaseOrderReference: new FormControl<string>(alloc.purchaseOrderReference, { nonNullable: true }),
          purchaseOrderNumber: new FormControl<string>(alloc.purchaseOrderNumber, { nonNullable: true }),
          selected: new FormControl<boolean>(true, { nonNullable: true }),
          allocatedAmount: new FormControl<number>(alloc.allocatedAmount, { nonNullable: true }),
          ordered: new FormControl<number>(0, { nonNullable: true }),
          // Saved with the allocation since #5373; hydrateOrderedTotals fills it from the PO for the
          // records written before that, so the column is never labelled with the funding account's.
          currency: new FormControl<Currency | null>(alloc.currency ?? null),
        }),
      );
    }
    this.recomputeTotals();
    // A saved allocation only stores its allocatedAmount, not the PO's ordered total â€” fetch the POs so the
    // "Ordered" column shows the real total (in the advance currency), not 0.
    this.hydrateOrderedTotals((data.allocations ?? []).map((a) => a.purchaseOrderReference));

    if (this.status() !== ApPaymentStatus.Draft) {
      this.form.disable({ emitEvent: false });
    } else if (this.fromRequest()) {
      // A request-spawned draft is entirely the request's to decide (#5373). What was approved is what we
      // send: the obligation is fixed at approval, what leaves the bank is derived from it and the account
      // funding it, and neither is this screen's to restate.
      //
      // The amount used to stay editable here, for #4457's "you may advance less than was requested". That
      // still holds as a need â€” it just belongs on the REQUEST, where approving less is a decision someone
      // signs off, rather than on the payment, where it silently changes how much debt is discharged.
      this.form.controls.provider.disable({ emitEvent: false });
      for (const alloc of this.allocations.controls) {
        alloc.disable({ emitEvent: false });
      }
    }
  }

  // ---- save / post ----
  private buildInsert(): ApAdvancePaymentInsert {
    const v = this.form.getRawValue();
    const allocations = this.allocations.controls
      .filter((r) => r.controls.selected.value && (r.controls.allocatedAmount.value ?? 0) > 0)
      .map((r) => ({ purchaseOrderReference: r.controls.purchaseOrderReference.value, allocatedAmount: r.controls.allocatedAmount.value }));

    // Exactly one leg is ours to state; the API derives the other (#5373). Keyed off the leg rather than
    // off whatever the control happens to hold: where no leg is asked for, what leaves IS the obligation,
    // and a figure left over from an earlier pairing of currencies is not an amount anyone stated.
    const leg = this.observedLeg();

    return {
      providerReference: v.provider?.id ?? '',
      financialCompanyReference: '', // API resolves from the current site
      paymentDate: (v.paymentDate ?? new Date()).toISOString(),
      amount: leg === 'departure' ? (v.observedAmount ?? 0) : this.obligationTotal(),
      destinationAmount: leg === 'arrival' ? (v.observedAmount ?? null) : null,
      transferFee: v.bankingFee ?? 0,
      securityTax: v.securityFee ?? 0,
      currency: v.currency,
      bankAccountReference: v.bankAccount ?? undefined,
      selectedProviderBankId: v.selectedProviderBankId ?? null,
      notes: v.notes ?? null,
      allocations,
    };
  }

  private validateForPost(): boolean {
    const conflict = this.destinationConflict();
    if (conflict) {
      GlobalMessageUtil.warn(this.t('ApAdvance_Conflict_Title', 'This advance cannot be paid to that account'), conflict, 8000);
      return false;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      reportInvalidFormFields(this.form, this.fieldLabels, this.translate);
      return false;
    }
    if (this.amount() <= 0) {
      this.reportNothingToSave();
      return false;
    }
    return true;
  }

  /** Draft save is lighter than post: provider + at least one allocation; the bank account is only
   * required at post time (an auto-created draft starts with none). */
  private validateForDraft(): boolean {
    if (!this.form.controls.provider.value) {
      this.form.controls.provider.markAsTouched();
      reportInvalidFormFields(this.form, this.fieldLabels, this.translate);
      return false;
    }
    if (this.amount() <= 0) {
      // Usually the stated leg: there is nothing to save until the officer says what left the bank, and
      // the button doing nothing at all is not an answer.
      this.form.controls.observedAmount.markAsTouched();
      this.reportNothingToSave();
      return false;
    }
    return true;
  }

  /** Says why the save was refused. Falls back to naming the allocation itself: with no purchase order
   *  selected the advance is worth nothing, and no control is invalid to report it. */
  private reportNothingToSave(): void {
    if (reportInvalidFormFields(this.form, this.fieldLabels, this.translate).length > 0) return;
    GlobalMessageUtil.warn(
      this.t('Form_Validation_MissingFieldsTitle', 'Missing information'),
      this.t('ApAdvance_NothingToSave', 'Select at least one purchase order and enter an amount to advance.'),
      5000,
    );
  }

  saveDraft(): void {
    if (!this.validateForDraft()) return;
    this.startLoading();
    const id = this.currentId();
    const obs = !this.isNew() && id ? this.service.updateDraft(id, this.buildInsert()) : this.service.createDraft(this.buildInsert());
    obs.pipe(finalize(() => this.stopLoading())).subscribe({
      next: (response) => {
        const saved = response.data;
        if (saved?.id) {
          this.isNew.set(false);
          this.currentId.set(saved.id);
          this.status.set(saved.status);
          this.advanceNumber.set(saved.number ?? '');
          this.router.navigate([], { queryParams: { id: saved.id }, replaceUrl: true });
        }
      },
      error: (error) => console.error('Error saving advance draft:', error),
    });
  }

  openRequest(): void {
    const id = this.requestId();
    if (id) this.router.navigate(['/ap-advance-request-details'], { queryParams: { id } });
  }

  /**
   * Opens the purchase order in the Order UI in a new tab.
   *
   * <p>The JWT is deliberately NOT passed as ?token=: it pushed the URL past what the edge accepts and the
   * navigation failed outright. The Order UI adopts the session it already holds; if it holds none, its guard
   * sends the user to login with a returnUrl and they land back on this purchase order.</p>
   */
  openPurchaseOrder(purchaseOrderReference: string | null | undefined): void {
    if (!purchaseOrderReference) return;
    const url = `${environment.domainUrl.order}/purchase-order-details?id=${purchaseOrderReference}`;
    window.open(url, '_blank');
  }

  /** A loaded/spawned advance stores only allocatedAmount per PO, not the PO's ordered total â€” batch-fetch
   * the POs so the "Ordered" column shows the real total (in the ORDER's currency) instead of 0. The same
   * fetch supplies the currency for allocations saved before it was stored on them (#5373). */
  private hydrateOrderedTotals(purchaseOrderReferences: (string | null | undefined)[]): void {
    const refs = Array.from(new Set(purchaseOrderReferences.filter((r): r is string => !!r)));
    if (refs.length === 0) return;
    this.purchaseOrderService.getByIds(refs).subscribe({
      next: (response) => {
        const byPo = new Map<string, { ordered: number; currency: Currency | null }>();
        for (const po of response.data ?? []) {
          if (!po.id) continue;
          const ordered = (po.items ?? []).reduce((sum, i) => sum + (i.totalPrice ?? 0), 0);
          byPo.set(po.id, { ordered: Math.round(ordered * 100) / 100, currency: (po.currency as Currency) ?? null });
        }
        for (const row of this.allocations.controls) {
          const po = byPo.get(row.controls.purchaseOrderReference.value);
          if (!po) continue;
          row.controls.ordered.setValue(po.ordered, { emitEvent: false });
          if (row.controls.currency.value === null && po.currency !== null) {
            row.controls.currency.setValue(po.currency, { emitEvent: false });
          }
        }
        // The rows changed silently (emitEvent:false), so nothing else would refresh the snapshot the
        // totals and the obligation currency are computed from.
        this.recomputeTotals();
      },
      error: () => undefined,
    });
  }

  post(): void {
    if (!this.validateForPost()) return;
    this.confirmationService.confirm({
      header: this.t('ApAdvance_Post_Confirm_Header', 'Post Advance'),
      message: this.t(
        'ApAdvance_Post_Confirm_Message',
        'Posting creates the journal entry (Dr Anticipo / Cr Bank) and moves the bank balance. Continue?',
      ),
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-success',
      accept: () => this.doPost(),
    });
  }

  private doPost(): void {
    const bankTransactionNumber = this.form.controls.bankTransactionReference.value ?? null;
    const existingId = this.currentId();
    this.startLoading();

    if (!this.isNew() && existingId) {
      this.service
        .post(existingId, { bankTransactionNumber })
        .pipe(finalize(() => this.stopLoading()))
        .subscribe({
          next: () => this.router.navigate(['/ap-advances']),
          error: (error) => console.error('Error posting advance:', error),
        });
      return;
    }

    this.service.createDraft(this.buildInsert()).subscribe({
      next: (response) => {
        const created = response.data;
        if (!created?.id) {
          this.stopLoading();
          return;
        }
        this.service
          .post(created.id, { bankTransactionNumber })
          .pipe(finalize(() => this.stopLoading()))
          .subscribe({
            next: () => this.router.navigate(['/ap-advances']),
            error: (error) => console.error('Error posting advance:', error),
          });
      },
      error: (error) => {
        this.stopLoading();
        console.error('Error creating advance before post:', error);
      },
    });
  }

  // ---- receipt (file comes from the shared bank-charges section) ----
  onReceiptFile(file: File): void {
    const id = this.currentId();
    if (!file || !id) return;
    this.startLoading();
    this.service
      .uploadReceipt(id, file)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          if (response.data) this.attachments.set(response.data.attachments ?? []);
        },
        error: (error) => console.error('Error uploading receipt:', error),
      });
  }

  openAttachment(url: string | null | undefined): void {
    if (url) window.open(url, '_blank');
  }

  back(): void {
    this.router.navigate(['/ap-advances']);
  }

  getRowGroup(row: unknown): FormGroup<ApAdvanceAllocationFormGroup> {
    return row as FormGroup<ApAdvanceAllocationFormGroup>;
  }

  attachmentFileName(name: string | null | undefined): string {
    if (!name) return '';
    const segments = name.split('/');
    return segments[segments.length - 1] || name;
  }

  /**
   * Deletes this draft, after confirming (#5223).
   *
   * <p>Offered only on a SAVED draft: there is nothing to delete before the first save. The API applies the
   * same draft-only rule, so a record that moved on in another tab is answered by that refusal rather than
   * by whatever this screen last painted â€” the button is a convenience, not the guard.</p>
   *
   * <p>Hard delete: a draft has posted nothing, so there is no ledger entry to keep and nothing a surviving
   * row would explain. Hence the explicit warning that it cannot be undone.</p>
   */
  confirmDelete(): void {
    const id = this.currentId();
    if (!id || !this.isDraft()) return;
    this.confirmationService.confirm({
      header: this.t('ApAdvance_Delete_Title', 'Delete draft'),
      message: this.t('ApAdvance_Delete_Confirm', 'Delete this draft advance? This cannot be undone.'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.t('Delete', 'Delete'),
      rejectLabel: this.t('Cancel', 'Cancel'),
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.deleteDraft(id),
    });
  }

  private deleteDraft(id: string): void {
    this.service.delete(id).subscribe({
      next: () => {
        GlobalMessageUtil.success(
          this.t('ApAdvance_Delete_Done_Title', 'Draft deleted'),
          this.t('ApAdvance_Delete_Done', 'The draft was deleted.'),
        );
        this.router.navigate(['/ap-advances']);
      },
    });
  }
}
